import {Material, MaterialType} from '@domain/materials'
import {dataTestId} from '@hconnect/uikit'
import ClearIcon from '@mui/icons-material/Clear'
import {
  FormHelperText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Box,
  Button
} from '@mui/material'
import {useMemo} from 'react'
import {Controller, Control, Path, FieldValues} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {ChipInput} from './ChipInput'

interface MaterialSelectionProps<T extends FieldValues> {
  control: Control<T>
  materials: Material[]
  selectedItem?: {upmId: string}
  name: Path<T>
  label: string
  optional?: boolean
  testId?: string
  materialType?: MaterialType | MaterialType[]
  showAllMaterials?: boolean
  translateOptions?: boolean
  classificationCode?: string
  classificationCodeValue?: string
  classificationMaterialType?: MaterialType
}

const filterAndMapFuels = (
  materialType?: MaterialType | MaterialType[],
  data?: Material[],
  classificationCode?: string,
  classificationCodeValue?: string,
  classificationMaterialType?: MaterialType
): Material[] => {
  if (!data || !materialType) return []

  const types = Array.isArray(materialType) ? materialType : [materialType]

  return data.filter((item) => {
    const hasMatchingMaterialType = types.includes(item.type)

    if (!hasMatchingMaterialType) {
      return false
    }

    const itemClassification = item.classificationCode

    const itemClassificationMaterialType = itemClassification?.MaterialType

    const shouldFilterByClassification = item.type === classificationMaterialType

    if (!shouldFilterByClassification) {
      return true
    }

    if (!itemClassification) {
      return false
    }

    if (
      itemClassificationMaterialType &&
      classificationMaterialType === itemClassificationMaterialType
    ) {
      if (!classificationCode) return false
      return itemClassification[classificationCode] === classificationCodeValue
    }

    return hasMatchingMaterialType
  })
}

export const MaterialSelection = <T extends FieldValues>({
  control,
  materials,
  label,
  testId,
  name,
  optional,
  materialType,
  showAllMaterials,
  translateOptions,
  classificationCode,
  classificationCodeValue,
  classificationMaterialType
}: MaterialSelectionProps<T>): JSX.Element => {
  const {t} = useTranslation()

  const filteredMaterials = useMemo(
    () =>
      showAllMaterials
        ? materials
        : filterAndMapFuels(
            materialType,
            materials,
            classificationCode,
            classificationCodeValue,
            classificationMaterialType
          ),
    [
      showAllMaterials,
      materials,
      materialType,
      classificationCode,
      classificationCodeValue,
      classificationMaterialType
    ]
  )

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required: {
          value: !optional,
          message: t('error.required')
        }
      }}
      render={({field: {ref, value, onChange}, fieldState: {error}}) => {
        const helperText = (error && error.message) || ''
        return (
          <FormControl error={!!error} fullWidth>
            <InputLabel id={`${label}-label`}>{t(label)}</InputLabel>
            <Select
              multiple
              labelId={`${label}-label`}
              label={t(label)}
              MenuProps={{
                MenuListProps: {disablePadding: true, sx: {paddingTop: '8px'}},
                PaperProps: {
                  style: {
                    maxHeight: 420
                  }
                }
              }}
              inputRef={ref}
              onChange={(e) => {
                const selectedValues = e.target.value
                onChange(selectedValues.join('";"'))
              }}
              {...dataTestId(testId || '')}
              value={value ? value.split('";"') : []}
              renderValue={(selected) => (
                <ChipInput
                  options={filteredMaterials
                    .filter((option) => selected.includes(option.id.toString()))
                    .map((option) => ({
                      id: option.id.toString(),
                      name: translateOptions
                        ? t(`plantSetup.configOptions.${option.name}`)
                        : option.name
                    }))}
                  onRemove={(option) =>
                    onChange(selected.filter((item) => item !== option).join('";"'))
                  }
                  key={`${label}-selected-materials`}
                />
              )}
            >
              {filteredMaterials.map((option) => {
                const currentValue = value ? value.split('";"') : []
                return (
                  <MenuItem key={`${label}-${option.id}`} value={option.id.toString()}>
                    <Checkbox checked={currentValue.includes(option.id.toString())} />
                    <ListItemText
                      primary={
                        translateOptions
                          ? t(`plantSetup.configOptions.${option.name}`)
                          : option.name
                      }
                    />
                  </MenuItem>
                )
              })}
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                paddingY={2}
                sx={{
                  position: 'sticky',
                  bottom: 0,
                  background: (theme) => theme.palette.background.paper,
                  boxShadow: '0 -2px 5px rgba(0,0,0,0.1)',
                  textAlign: 'center'
                }}
              >
                <Button sx={{width: '90%'}} onClick={() => onChange('')} color="secondary">
                  <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                    <ClearIcon color="secondary" />
                    {t('plantSetup.clearAll')}
                  </Box>
                </Button>
              </Box>
            </Select>
            <FormHelperText error={!!error}>{helperText}</FormHelperText>
          </FormControl>
        )
      }}
    />
  )
}
